import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

const Toast = ({ message, type, onClose }) => (
    <div className={`px-6 py-4 rounded-xl shadow-plantlix-xl animate-slideIn backdrop-blur-md border-2 ${type === 'success'
            ? 'bg-plantlix-500/95 border-plantlix-400 text-white'
            : type === 'error'
                ? 'bg-red-500/95 border-red-400 text-white'
                : 'bg-plantlix-500/95 border-plantlix-400 text-white'
        }`}>
        <div className="flex items-center gap-3">
            <span className="text-2xl">
                {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'}
            </span>
            <span className="font-medium flex-1">{message}</span>
            <button
                onClick={onClose}
                className="ml-2 text-white/80 hover:text-white text-2xl leading-none transition-colors"
                aria-label="Close"
            >
                ×
            </button>
        </div>
    </div>
);

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'success', duration = 3000) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, addToast: showToast }}>
            {children}
            <div className="fixed top-4 right-4 z-[99999] space-y-2 pointer-events-none">
                {toasts.map(toast => (
                    <div key={toast.id} className="pointer-events-auto">
                        <Toast {...toast} onClose={() => removeToast(toast.id)} />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};
